import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

const TableControls = ({
  rowsPerPage,
  setRowsPerPage,
  searchTerm,
  setSearchTerm,
  handlePrint,
  availableRowsPerPage = ["ALL", 10, 25, 50, 100],
}) => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      marginBottom: "20px",
      marginTop: "10px",
      width: "100%",
      position: "relative",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "middle",
        position: "absolute",
        left: "50%",
        transform: "translateX(-50%)",
      }}
    >
      <label htmlFor="per-page-select" style={{ marginRight: "5px" }}>
        Per page
      </label>
      <select
        id="per-page-select"
        value={rowsPerPage}
        onChange={(e) =>
          setRowsPerPage(e.target.value === "ALL" ? "ALL" : Number(e.target.value))
        }
        style={{ padding: "5px", border: "1px solid #ccc", borderRadius: "3px" }}
      >
        {availableRowsPerPage.map((rows) => (
          <option key={rows} value={rows}>
            {rows}
          </option>
        ))}
      </select>
    </div>

    <div
      style={{
        display: "flex",
        alignItems: "center",
        position: "absolute",
        right: 0,
      }}
    >
      <button
        disabled
        style={{
          marginRight: "5px",
          padding: "8px 10px",
          backgroundColor: "#ccc",
          border: "none",
          borderRadius: "3px",
          cursor: "not-allowed",
          opacity: 0.6,
        }}
      ></button>
      <button
        disabled
        style={{
          marginRight: "10px",
          padding: "8px 10px",
          backgroundColor: "#ccc",
          border: "none",
          borderRadius: "3px",
          cursor: "not-allowed",
          opacity: 0.6,
        }}
      ></button>
      <button
        onClick={handlePrint}
        style={{
          marginRight: "15px",
          padding: "8px 15px",
          backgroundColor: "#0056b3",
          color: "white",
          border: "none",
          borderRadius: "3px",
          cursor: "pointer",
          fontWeight: "bold",
        }}
      >
        Print
      </button>
      <input
        type="text"
        placeholder="Search"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{
          padding: "8px",
          border: "1px solid #ccc",
          borderRadius: "3px",
          width: "200px",
        }}
      />
    </div>
  </div>
);

const Shop_Wise_NFSA_State_Sales_Details = ({ nfsaType, month, year, distCode }) => {
  const [schemeList, setSchemeList] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");

  const monthNames = [
    "January","February","March","April","May","June","July",
    "August","September", "October", "November","December",
  ];
  const monthName = monthNames[month - 1] || "";

  const handleSetRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  useEffect(() => {
    const fetchSchemes = async () => {
      if (!month || !year || !nfsaType) return;
      try {
        const res = await axios.get(`${process.env.REACT_APP_URL}/api/fpsSales/schemes`, {
          params: { month, year, nfsaType },
        });
        setSchemeList(res.data || []);
      } catch (err) {
        console.error("Error fetching scheme list:", err);
      }
    };
    fetchSchemes();
  }, [month, year, nfsaType]);

  useEffect(() => {
    const fetchSales = async () => {
      if (!distCode || schemeList.length === 0) return;
      setLoading(true);
      try {
        const res = await axios.post(
         `${process.env.REACT_APP_URL}/api/fpsSales/afsoShopSale`,
          schemeList,
          { params: { nfsaType, month, year, distCode } }
        );
        setSalesData(res.data || []);
      } catch (err) {
        console.error("Error fetching sales data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSales();
  }, [schemeList, distCode, nfsaType, month, year]);

  const filteredSalesData = useMemo(() => {
    if (!searchTerm) return salesData;
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    return salesData.filter(
      (item) =>
        item.distNameEn?.toLowerCase().includes(lowerCaseSearchTerm) ||
        item.afsoNameEn?.toLowerCase().includes(lowerCaseSearchTerm) ||
        item.fpsId?.toString().toLowerCase().includes(lowerCaseSearchTerm)
    );
  }, [salesData, searchTerm]);

  const totals = useMemo(() => {
    const calculatedTotals = {};
    schemeList.forEach((scheme) => {
      calculatedTotals[scheme.schemeId] = filteredSalesData.reduce(
        (sum, item) => sum + (item.schemeWiseSales?.[scheme.schemeId] || 0),
        0
      );
    });
    const extraTotal =
      nfsaType === "N"
        ? filteredSalesData.reduce((sum, item) => sum + (item.nfsaExtraQty || 0), 0)
        : 0;
    const grandTotal = filteredSalesData.reduce((sum, item) => sum + (item.total || 0), 0);
    return { ...calculatedTotals, extraTotal, grandTotal };
  }, [filteredSalesData, schemeList, nfsaType]);

  const totalEntries = filteredSalesData.length;
  const totalDataEntries = salesData.length;
  const totalColumns = 4 + schemeList.length + (nfsaType === "N" ? 2 : 1);
  const totalPages = rowsPerPage === "ALL" ? 1 : Math.ceil(totalEntries / rowsPerPage);
  const startIndex = totalEntries === 0 ? 0 : (currentPage - 1) * (rowsPerPage === "ALL" ? totalEntries : rowsPerPage);
  const endIndex =
    totalEntries === 0
      ? 0
      : Math.min(startIndex + (rowsPerPage === "ALL" ? totalEntries : rowsPerPage), totalEntries);
  const currentRows =
    rowsPerPage === "ALL" ? filteredSalesData : filteredSalesData.slice(startIndex, endIndex);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    } else if (currentPage > totalPages && totalPages === 0) {
      setCurrentPage(1);
    }
  }, [filteredSalesData.length, totalPages, currentPage]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const pageTotals = useMemo(() => {
    const calculatedPageTotals = {};
    schemeList.forEach((scheme) => {
      calculatedPageTotals[scheme.schemeId] = currentRows.reduce(
        (sum, item) => sum + (item.schemeWiseSales?.[scheme.schemeId] || 0),
        0
      );
    });
    const pageExtraTotal =
      nfsaType === "N"
        ? currentRows.reduce((sum, item) => sum + (item.nfsaExtraQty || 0), 0)
        : 0;
    const pageGrandTotal = currentRows.reduce(
      (sum, item) => sum + (item.total || 0),
      0
    );
    return { ...calculatedPageTotals, pageExtraTotal, pageGrandTotal };
  }, [currentRows, schemeList, nfsaType]);

  const renderPagination = () => {
    const pages = [];
    const visiblePages = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + visiblePages - 1);
    if (end - start < visiblePages - 1) start = Math.max(1, end - visiblePages + 1);

    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => goToPage(i)}
          style={{
            margin: "0 2px",
            padding: "3px 7px",
            backgroundColor: currentPage === i ? "#0e16e2ff" : "#2714d1ff",
            color: "white",
            border: "none",
            borderRadius: "3px",
            cursor: "pointer",
            fontSize: "13px",
            textDecoration: "underline",
          }}
        >
          {i}
        </button>
      );
    }

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "15px",
          padding: "8px 0",
          fontSize: "14px",
        }}
      >
        <div style={{ color: "#8B0000", fontWeight: "bold" }}>
          Showing <b>{startIndex}</b> to <b>{endIndex}</b> of <b>{totalEntries}</b> entries
          {totalEntries !== totalDataEntries && (
            <> (filtered from {totalDataEntries} total entries)</>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center" }}>
          <button
            onClick={() => goToPage(1)}
            disabled={currentPage === 1}
            style={{
              margin: "0 2px",
              padding: "3px 6px",
              backgroundColor: "#0219e2d6",
              color: "white",
              border: "none",
              borderRadius: "3px",
              fontSize: "13px",
              textDecoration: "underline",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              opacity: currentPage === 1 ? 0.6 : 1,
            }}
          >
            First
          </button>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              margin: "0 2px",
              padding: "3px 6px",
              backgroundColor: "#0715e0e9",
              color: "white",
              border: "none",
              borderRadius: "3px",
              fontSize: "13px",
              textDecoration: "underline",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              opacity: currentPage === 1 ? 0.6 : 1,
            }}
          >
            Previous
          </button>
          {pages}
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages || totalPages === 0}
            style={{
              margin: "0 2px",
              padding: "3px 6px",
              backgroundColor: "rgba(10, 10, 217, 1)",
              color: "white",
              border: "none",
              borderRadius: "3px",
              fontSize: "13px",
              textDecoration: "underline",
              cursor:
                currentPage === totalPages || totalPages === 0 ? "not-allowed" : "pointer",
              opacity: currentPage === totalPages || totalPages === 0 ? 0.6 : 1,
            }}
          >
            Next
          </button>
          <button
            onClick={() => goToPage(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
            style={{
              margin: "0 2px",
              padding: "3px 6px",
              backgroundColor: "#1208c1ff",
              color: "white",
              border: "none",
              borderRadius: "3px",
              fontSize: "13px",
              textDecoration: "underline",
              cursor:
                currentPage === totalPages || totalPages === 0 ? "not-allowed" : "pointer",
              opacity: currentPage === totalPages || totalPages === 0 ? 0.6 : 1,
            }}
          >
            Last
          </button>
        </div>
      </div>
    );
  };

  const handleDownloadExcel = () => {
    const headerRow = [
      "Sl No",
      "District",
      "AFSO Name",
      "FPS ID",
      ...schemeList.map((s) => s.schemeNameEn),
      ...(nfsaType === "N" ? ["NFSA Extra Qty"] : []),
      "Total",
    ];

    const dataRows = filteredSalesData.map((item, idx) => [
      idx + 1,
      item.distNameEn,
      item.afsoNameEn,
      item.fpsId,
      ...schemeList.map((s) => item.schemeWiseSales?.[s.schemeId] || 0),
      ...(nfsaType === "N" ? [item.nfsaExtraQty || 0] : []),
      item.total || 0,
    ]);

    const grandTotalRow = [
      "Grand Total",
      "",
      "",
      "",
      ...schemeList.map((s) => totals[s.schemeId].toFixed(3)),
      ...(nfsaType === "N" ? [totals.extraTotal.toFixed(3)] : []),
      totals.grandTotal.toFixed(3),
    ];

    const wsData = [[reportTitle], [], headerRow, ...dataRows, grandTotalRow];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sales Report");
    XLSX.writeFile(wb, `NFSA_Sales_Report_${monthName}_${year}.xlsx`);
  };

  const handlePrint = () => {
    window.print();
  };

  const reportTitle = `Monthly Allotted Sales of Rice for ${
    nfsaType === "N" ? "NFSA" : "State"
  } in ${monthName} '${year}`;

  if (loading)
    return <p className="text-center mt-4 text-gray-600">Loading data...</p>;

  const titleStyle = {
    backgroundColor: "#000",
    color: "#fff",
    padding: "12px 0",
    fontSize: "18px",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: "0",
  };

  return (
    <div style={{ marginBottom: "40px", backgroundColor: "white" }}>
      <TableControls
        rowsPerPage={rowsPerPage}
        setRowsPerPage={handleSetRowsPerPage}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handlePrint={handlePrint}
      />

<div style={{ ...titleStyle, width: "88%", margin: "0 auto" }}>{reportTitle}</div>

      <div className="overflow-x-auto" style={{ width: "88%", margin: "0 auto" }}>
  <table
    className="w-full text-sm border"
    style={{
      borderCollapse: "collapse",
      marginTop: 0,
      fontSize: "12px"
    }}
  >
          <thead>
            <tr style={{ backgroundColor: "#000", color: "#fff" }}>
              <th rowSpan="2" className="border p-2">
                Sl No
              </th>
              <th rowSpan="2" className="border p-2">
                District
              </th>
              <th rowSpan="2" className="border p-2">
                AFSO Name
              </th>
              <th rowSpan="2" className="border p-2">
                FPS ID
              </th>
              <th colSpan={totalColumns - 4} className="border p-2 text-center">
                {nfsaType === "N"
                  ? "NFSA (Quantity of Allotted Rice (Kg))"
                  : "State (Quantity of Allotted Rice (Kg))"}
              </th>
            </tr>
            <tr style={{ backgroundColor: "#222", color: "#fff" }}>
              {schemeList.map((scheme) => (
                <th key={scheme.schemeId} className="border p-2">
                  {scheme.schemeNameEn}
                </th>
              ))}
              {nfsaType === "N" && <th className="border p-2">NFSA Extra Qty</th>}
              <th className="border p-2">Total</th>
            </tr>
          </thead>

          <tbody>
            {currentRows.length > 0 ? (
              currentRows.map((item, idx) => (
                <tr key={startIndex + idx}>
                  <td className="border p-2 text-center">{startIndex + idx + 1}</td>
                  <td className="border p-2">{item.distNameEn}</td>
                  <td className="border p-2">{item.afsoNameEn}</td>
                  <td className="border p-2">{item.fpsId}</td>

                  {schemeList.map((scheme) => (
                    <td key={scheme.schemeId} className="border p-2 text-right">
                      {(item.schemeWiseSales?.[scheme.schemeId] || 0).toFixed(3)}
                    </td>
                  ))}

                  {nfsaType === "N" && (
                    <td className="border p-2 text-right">{(item.nfsaExtraQty || 0).toFixed(3)}</td>
                  )}
                  <td className="border p-2 font-semibold text-right">{(item.total || 0).toFixed(3)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={totalColumns} className="border p-2 text-center" style={{ color: "#000" }}>
                  No matching records found
                </td>
              </tr>
            )}

            <tr style={{ backgroundColor: "#000", color: "#fff", fontWeight: "bold" }}>
              <td colSpan="4" className="border p-2 text-center">
                Total
              </td>
              {schemeList.map((scheme) => (
                <td key={scheme.schemeId} className="border p-2 text-right">
                  {pageTotals[scheme.schemeId].toFixed(3)}
                </td>
              ))}
              {nfsaType === "N" && (
                <td className="border p-2 text-right">{pageTotals.pageExtraTotal.toFixed(3)}</td>
              )}
              <td className="border p-2 text-right">{pageTotals.pageGrandTotal.toFixed(3)}</td>
            </tr>

            <tr>
              <td colSpan={totalColumns} style={{ textAlign: "center", padding: "2px" }}>
                <button
                  onClick={handleDownloadExcel}
                  style={{
                    backgroundColor: "#000",
                    color: "#fff",
                    padding: "10px 16px",
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontWeight: "bold",
                    paddingTop: "5px",
                    paddingBottom: "5px"
                  }}
                >
                  Download to Excel
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        {renderPagination()}
      </div>
    </div>
  );
};

export default Shop_Wise_NFSA_State_Sales_Details;
